import { ORDENES_DE_BUSQUEDA, type OrdenDeBusqueda, type TarjetaProfesional } from '@medchoice/shared';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import type { ScreenProps } from '../../routes/types';
import { Avatar, Boton, Cargando, EstadoVacio, Estrellas, FalloDeCarga, Icono, Selector, Tarjeta } from '../../ui';
import { cuandoEs, pesos } from '../../ui/format';
import { Buscador } from './Buscador';

const TEXTO_ORDEN: Record<OrdenDeBusqueda, string> = {
  relevancia: 'Más relevantes',
  primer_turno: 'Primer turno disponible',
  puntaje: 'Mejor puntuados',
  precio: 'Menor precio',
};

type Filtro = 'texto' | 'especialidad' | 'barrio' | 'obra_social' | 'orden';

export function Resultados({ consulta }: ScreenProps) {
  const filtros = {
    texto: consulta.get('texto') ?? undefined,
    especialidad: consulta.get('especialidad') ?? undefined,
    barrio: consulta.get('barrio') ?? undefined,
    obra_social: consulta.get('obra_social') ?? undefined,
    orden: (consulta.get('orden') as OrdenDeBusqueda | null) ?? undefined,
  };
  const { datos: catalogos } = usePedido(() => publico.catalogos(), []);
  const pedido = usePedido(() => publico.buscar(filtros), [consulta.toString()]);

  // Los filtros viven en la dirección: se pueden compartir y el botón atrás funciona.
  const cambiar = (filtro: Filtro, valor: string) => {
    const nueva = new URLSearchParams(consulta);
    if (valor) nueva.set(filtro, valor); else nueva.delete(filtro);
    irA(`/buscar${nueva.size ? `?${nueva.toString()}` : ''}`, { reemplazar: true });
  };
  const hayFiltros = Boolean(filtros.texto || filtros.especialidad || filtros.barrio || filtros.obra_social);

  return (
    <div className="resultados">
      <header className="resultados__encabezado">
        <h1>{filtros.texto ? `Resultados para “${filtros.texto}”` : 'Buscá un profesional'}</h1>
        {pedido.datos && <p>{pedido.datos.length} {pedido.datos.length === 1 ? 'profesional verificado' : 'profesionales verificados'}</p>}
      </header>

      <Buscador key={filtros.texto ?? ''} inicial={filtros.texto} enfocar={!filtros.texto}
        alBuscar={(texto) => cambiar('texto', texto)} />

      <div className="resultados__filtros">
        <Selector etiqueta="Especialidad" marcador="Todas" value={filtros.especialidad ?? ''}
          opciones={catalogos?.especialidades ?? []} onChange={(e) => cambiar('especialidad', e.target.value)} />
        <Selector etiqueta="Barrio" marcador="Todos" value={filtros.barrio ?? ''}
          opciones={catalogos?.barrios ?? []} onChange={(e) => cambiar('barrio', e.target.value)} />
        <Selector etiqueta="Obra social" marcador="Todas" value={filtros.obra_social ?? ''}
          opciones={catalogos?.obras_sociales ?? []} onChange={(e) => cambiar('obra_social', e.target.value)} />
        <Selector etiqueta="Ordenar por" value={filtros.orden ?? 'relevancia'}
          opciones={ORDENES_DE_BUSQUEDA.map((o) => ({ valor: o, texto: TEXTO_ORDEN[o] }))}
          onChange={(e) => cambiar('orden', e.target.value)} />
        {hayFiltros && <Boton variante="fantasma" onClick={() => irA('/buscar')}>Limpiar filtros</Boton>}
      </div>

      {pedido.estado === 'cargando' && <Cargando texto="Buscando profesionales" />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {pedido.estado === 'listo' && pedido.datos?.length === 0 && (
        <EstadoVacio icono="search" titulo="No encontramos profesionales"
          cuerpo="Probá con otras palabras o sacá algún filtro."
          accion={<Boton variante="secundario" onClick={() => irA('/buscar')}>Limpiar filtros</Boton>} />
      )}
      <div className="resultados__lista">
        {pedido.datos?.map((p) => <TarjetaDeProfesional key={p.id_profesional} profesional={p} />)}
      </div>
    </div>
  );
}

function TarjetaDeProfesional({ profesional: p }: { profesional: TarjetaProfesional }) {
  const nombre = `${p.nombre} ${p.apellido}`;
  return (
    <Tarjeta resaltable>
      <div className="tarjeta-profesional">
        <Avatar nombre={nombre} tamano={64} verificado />
        <div className="tarjeta-profesional__datos">
          <h2>{nombre}</h2>
          <p className="tarjeta-profesional__meta">{p.especialidad} · {p.matricula}</p>
          <Estrellas puntaje={p.calificacion ?? 0} cantidad={p.cantidad_resenas} />
          <p className="tarjeta-profesional__lugar"><Icono nombre="map-pin" tamano={14} /> {p.direccion}, {p.barrio}</p>
          <div className="chips">{p.obras_sociales.map((o) => <span key={o} className="chip">{o}</span>)}</div>
        </div>
        <div className="tarjeta-profesional__accion">
          {p.precio_desde !== null && <span className="tarjeta-profesional__precio">Desde {pesos(p.precio_desde)}</span>}
          {p.proximo_turno && (
            <span className="tarjeta-profesional__turno">
              <Icono nombre="clock" tamano={14} /> {cuandoEs(p.proximo_turno.fecha)} {p.proximo_turno.hora}
            </span>
          )}
          <Boton onClick={() => irA(`/profesional/${p.id_profesional}`)}>Ver perfil</Boton>
        </div>
      </div>
    </Tarjeta>
  );
}
